/**
 * InvoicesPanel — the Invoices section of Settings › Billing.
 *
 * In-app Stripe invoice history (GET /v1/billing/invoices): date,
 * number, status, amount and a PDF / hosted-page link per invoice,
 * newest first, with cursor-based "Load more" pagination. Card and
 * subscription management stays in the Stripe portal ("Manage
 * billing" in the balance strip).
 *
 * Fetched through the ``billing_invoices`` saga slice; the panel
 * accumulates pages locally (the saga echoes the request cursor back
 * so we know whether a response is a first page or a "load more").
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Box, CircularProgress, IconButton, Link, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  fetchRequest as fetchInvoicesRequest,
  clearState as clearInvoices,
} from '../../redux/actions/billing/invoices';
import { Surface } from '../dashboard/components/DashboardPrimitives';
import StatusChip from '../common/StatusChip';
import StripePortalLink from './StripePortalLink';

const PAGE_SIZE = 24;

// Stripe invoice status -> StatusChip tone.
const STATUS_TONE = {
  paid: 'success',
  open: 'warning',
  draft: 'neutral',
  void: 'neutral',
  uncollectible: 'error',
};

const fmtAmount = (minorUnits, currency) => {
  if (minorUnits == null) return '—';
  const amount = Number(minorUnits) / 100;
  try {
    return amount.toLocaleString(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    });
  } catch (_e) {
    return `${amount.toFixed(2)} ${String(currency || '').toUpperCase()}`;
  }
};

const fmtDate = (unixTs) => {
  if (!unixTs) return '—';
  return new Date(unixTs * 1000).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const InvoicesPanel = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    response,
    isLoading,
    error,
  } = useSelector((s) => s.billing_invoices);

  // Pages accumulate locally: the saga replies with one page at a
  // time plus the cursor it was asked for, so the panel appends on
  // load-more responses and replaces on first-page responses.
  const [invoices, setInvoices] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [stripeConfigured, setStripeConfigured] = useState(true);
  const [loaded, setLoaded] = useState(false);

  // Clear first so a remount shows the spinner and refetches instead
  // of reacting to a stale page from a previous visit.
  useEffect(() => {
    dispatch(clearInvoices());
    dispatch(fetchInvoicesRequest({ startingAfter: null, limit: PAGE_SIZE }));
  }, [dispatch]);

  useEffect(() => {
    if (!response) return;
    if (response.starting_after) {
      setInvoices((prev) => [...prev, ...(response.invoices || [])]);
    } else {
      setInvoices(response.invoices || []);
      setStripeConfigured(response.stripe_configured !== false);
    }
    setHasMore(Boolean(response.has_more));
    setLoaded(true);
  }, [response]);

  const loadingMore = isLoading && loaded;
  const loading = !loaded && !error;

  const loadMore = () => {
    const last = invoices[invoices.length - 1];
    if (!last) return;
    dispatch(fetchInvoicesRequest({ startingAfter: last.id, limit: PAGE_SIZE }));
  };

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }
  if (error) {
    return <Alert severity="error">Failed to load invoices: {String(error)}</Alert>;
  }

  return (
    <Surface sx={{ p: 2.5 }}>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{
            width: 28, height: 28, borderRadius: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(theme.palette.primary.main, 0.12),
            color: 'primary.main',
          }}>
            <ReceiptIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography variant="overline" sx={{ fontSize: 10, letterSpacing: 1, fontWeight: 600, color: 'text.secondary' }}>
            Invoices
          </Typography>
        </Stack>
        <StripePortalLink
          size="small"
          variant="text"
          label="Manage billing"
          tooltip="Update your card, change the subscription, or manage payment details in the Stripe portal."
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Every invoice issued for this account — subscription renewals and
        credit top-ups. Download the PDF or open the hosted invoice page.
      </Typography>

      {!stripeConfigured && (
        <Alert severity="info">
          Payments aren&apos;t configured in this environment, so there are no
          invoices to show.
        </Alert>
      )}

      {stripeConfigured && invoices.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No invoices yet — they appear here after your first subscription
          payment or credit top-up.
        </Typography>
      )}

      {invoices.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Invoice</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Download</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                  {fmtDate(inv.created)}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {inv.number || inv.id}
                  </Typography>
                  {inv.description && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                      {inv.description}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <StatusChip
                    status={inv.status || 'unknown'}
                    tone={STATUS_TONE[inv.status] || 'neutral'}
                  />
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {fmtAmount(inv.total, inv.currency)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {inv.invoice_pdf && (
                    <Tooltip title="Download PDF" arrow>
                      <IconButton
                        size="small"
                        component="a"
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <PictureAsPdfIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {inv.hosted_invoice_url && (
                    <Tooltip title="Open hosted invoice" arrow>
                      <IconButton
                        size="small"
                        component="a"
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <OpenInNewIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {!inv.invoice_pdf && !inv.hosted_invoice_url && '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Box>
      )}

      {hasMore && (
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={loadMore}
            sx={{ fontWeight: 600 }}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Link>
        </Box>
      )}
    </Surface>
  );
};

export default InvoicesPanel;
